import {
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import {
  useForm,
  SubmitHandler,
} from 'react-hook-form';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import api from '../../services/api.service';
import {
  FormalEducationData,
  FormalEducationResponseData,
} from '../../types/pdp.types';
import FormalEducationModal from './FormalEducationModal';
import EditButton from '../generic/EditButton';
import DeleteButton from '../generic/DeleteButton';
import ConfirmModal from '../generic/ConfirmModal';
import { formalEducationState } from '../../services/state.service';
import { FormStepProps } from '../../types/generic.types';

function FormalEducationTemplate({ formSubmitted }: FormStepProps): JSX.Element {
  const { t } = useTranslation();
  const [allEducationData, setAllEducationData] = useState<FormalEducationData[]>([]);
  const [currentEducation, setCurrentEducation] = useRecoilState<FormalEducationData>(formalEducationState);
  const [showNewEdit, setShowNewEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [refreshEducationData, setRefreshEducationData] = useState(false);
  interface EmptyForm {result: boolean}
  const { handleSubmit } = useForm<EmptyForm>();

  useEffect(() => {
    api.get('profile/education/').then(
      (response: FormalEducationResponseData) => {
        setAllEducationData(response.data);
      },
    );
  }, [currentEducation, refreshEducationData]);

  const closeNewEdit = useCallback((result: boolean, education: FormalEducationData): void => {
    if (result && education) {
      if (education.id > 0) {
        api.put(`/profile/education/${education.id}/`, education).then(() => {
          setShowNewEdit(false);
          setRefreshEducationData((r: boolean) => !r);
        });
      } else {
        api.post('profile/education/', education).then(() => {
          setShowNewEdit(false);
          setRefreshEducationData((r: boolean) => !r);
        });
      }
    } else {
      setShowNewEdit(false);
    }
  }, []);

  const closeDelete = useCallback((result: boolean): void => {
    if (result && currentEducation) {
      api.delete(`/profile/education/${currentEducation.id}/`).then(() => {
        setShowDelete(false);
        setRefreshEducationData((r: boolean) => !r);
      });
    } else {
      setShowDelete(false);
    }
  }, [currentEducation]);

  const onSubmit: SubmitHandler<EmptyForm> = () => {
    formSubmitted();
  };

  return (
    <div>
      <FormalEducationModal
        show={showNewEdit}
        selectedEducation={currentEducation}
        callback={closeNewEdit}
      />
      <ConfirmModal
        show={showDelete}
        title={t('PDP.Confirm')}
        description={t('Formal_Education_Confirmation_Message')}
        yesLabel={t('PDP.Delete')}
        noLabel={t('PDP.Cancel')}
        callback={closeDelete}
      />
      <div className="title text-center">{t('Education_Title')}</div>
      <div className="col-lg-8 col-12 mx-auto">
        {t('Education_Description')}
      </div>
      <br />
      <div className="d-flex">
        <Button
          onClick={() => {
            setCurrentEducation({
              id: 0,
              credentials: '',
              school: '',
              city: '',
              state: '',
              country: '',
              start: '',
              end: '',
              current: false,
              continuingEducation: '',
            });
            setShowNewEdit(true);
          }}
          className="mb-3 ms-auto createbutton"
          variant="primary"
          size="sm"
          active
        >
          {`+ ${t('Formal_Education_Add')}`}
        </Button>
      </div>
      <div className="border border-dark rounded-3">
        <Form id="formaleducation-form" onSubmit={handleSubmit(onSubmit)}>
          <Table responsive hover className="mb-3 linktable">
            <thead>
              <tr className="linktableheader">
                <th className="w-20">
                  {t('Formal_Education_credentials')}
                </th>
                <th className="w-20">
                  {t('Formal_Education_school')}
                </th>
                <th className="w-20">
                  {t('PDP.Start_Date')}
                </th>
                <th className="w-20">
                  {t('PDP.End_Date')}
                </th>
                <th>
                  {t('PDP.Links_Table_Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {allEducationData.map((education: FormalEducationData) => (
                <tr key={education.id} className="dividinglines">
                  <th>
                    {education.credentials}
                  </th>
                  <th>
                    {education.school}
                  </th>
                  <th>
                    {education.start}
                  </th>
                  <th>
                    {education.end}
                  </th>
                  <th>
                    <div>
                      <EditButton
                        label=""
                        handler={() => {
                          setCurrentEducation(education);
                          setShowNewEdit(true);
                        }}
                      />
                    &nbsp;
                    &nbsp;
                      <DeleteButton
                        label=""
                        handler={() => {
                          setCurrentEducation(education);
                          setShowDelete(true);
                        }}
                      />
                    </div>
                  </th>
                </tr>
              ))}
            </tbody>
          </Table>
        </Form>
        <br />
      </div>
      <br />
    </div>
  );
}

export default FormalEducationTemplate;
